/**
 * Tax software options with their display information
 */
export const TAX_SOFTWARE_OPTIONS = [
  {
    id: "taxwise",
    name: "TaxWise",
    supported: true,
    color: "#32a852", //lime green
  },
  {
    id: "taxslayer",
    name: "TaxSlayer Web",
    supported: true,
    color: "#b30c0c", //red
  },
  {
    id: "taxslayer_desktop",
    name: "TaxSlayer Desktop",
    supported: false,
    color: "#b30c0c", //red
  },
  {
    id: "drake",
    name: "Drake Online",
    supported: false,
    color: "#0077CC",
    // Drake Online is an early-access integration — flag it so the UI shows an
    // "Early Access" label (purchase gating stays on the DRAKE_PURCHASE flag).
    earlyAccess: true,
  },
  {
    id: "drake_desktop",
    name: "Drake Desktop",
    supported: false,
    color: "#0077CC",
    // Drake Desktop is an early-access integration — flag it so the UI shows an
    // "Early Access" label (purchase gating lives on DRAKE_DESKTOP_PURCHASE).
    earlyAccess: true,
  },
  {
    id: "oltpro",
    name: "OLT Pro",
    supported: false,
    color: "#804fb0", //purple
    // OLT is a fresh integration — flag it so the UI can show an "Early Access" label.
    earlyAccess: true,
  },
  {
    id: "crosslink",
    name: "CrossLink",
    supported: true,
    color: "#857a03",
    // Purchase gating lives on the CROSSLINK_PURCHASE flag.
  },
  {
    id: "other",
    name: "Other",
    supported: false,
    color: "#0077CC",
  },
] as const;

/**
 * Native SmartWiz intake option id.
 * Distinct from the "bring your own" document-software integrations below —
 * this is the built-in Smart Intake taxpayer organizer, not an external system.
 */
export const SMARTWIZ_INTAKE_OPTION_ID = "smartwiz";

/**
 * Document management software options
 * Ordered by: native Smart Intake first (recommended), then external
 * integrations they can connect, then Other.
 */
export const DOCUMENT_SOFTWARE_OPTIONS = [
  {
    id: SMARTWIZ_INTAKE_OPTION_ID,
    name: "Smart Intake",
    supported: true,
    color: "#228BE6",
  },
  {
    id: "jotform",
    name: "JotForm",
    supported: false,
    color: "#FF6C00",
  },
  {
    id: "cognito",
    name: "Cognito",
    supported: false,
    color: "#FD79A8",
  },
  {
    id: "suitedash",
    name: "SuiteDash",
    supported: false,
    color: "#4A90E2",
  },
  {
    id: "ghl",
    name: "GHL (Go High Level)",
    supported: false,
    color: "#6C5CE7",
  },
  {
    id: "taxdome",
    name: "TaxDome",
    supported: false,
    color: "#00B894",
  },
  {
    id: "googledrive",
    name: "Google Drive",
    supported: false,
    color: "#4285F4",
  },
  {
    // Connectable intake integration in the extension (has a source module);
    // keep its id aligned with the extension's IntegrationIntakeName key.
    id: "taxestogo",
    name: "Taxes To Go",
    supported: false,
    color: "#0CA678",
  },
  {
    id: "taxshield",
    name: "TaxShield",
    supported: false,
    color: "#1A73E8",
  },
  {
    id: "other",
    name: "Other",
    supported: false,
    color: "#6B7280",
  },
] as const;

/**
 * Get the display name for a given tax software ID
 * :param softwareId: The software identifier
 * :return: The display name or the ID if not found
 */
export const getTaxSoftwareDisplayName = (softwareId?: string): string => {
  if (!softwareId) return "your selected software";
  const software = TAX_SOFTWARE_OPTIONS.find((s) => s.id === softwareId);
  return software?.name || softwareId;
};

/**
 * Get the display name for a given intake software ID
 * :param softwareId: The software identifier
 * :return: The display name or the ID if not found
 */
export const getIntakeSoftwareDisplayName = (softwareId?: string): string => {
  if (!softwareId) return "your selected software";
  const software = DOCUMENT_SOFTWARE_OPTIONS.find((s) => s.id === softwareId);
  return software?.name || softwareId;
};
